import { AxiosInstance } from 'axios'
import { EmptyObject, ID, PaginatedResult } from 'types/utility'
import {
  BulkInviteResponse,
  Invitation,
  InvitePayload,
  MembersInviteResponse
} from 'types/invitations'
import { INVITES_ENDPOINTS } from 'api/ApiRoutes'
import { Member } from 'types/members'
import { Role } from 'types/misc'
import { Group, Team } from 'types/groups'
import { Program } from 'types/programs'

class InvitesApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getInvitations = async ({
    page = 1,
    pageSize = 10,
    q = ''
  }: {
    page?: number
    pageSize?: number
    q?: string
  }) => {
    const queryString = new URLSearchParams({
      page_size: pageSize + '',
      page: page + '',
      q
    }).toString()
    const { data } = await this.axiosInstance.get<PaginatedResult<Invitation>>(
      `${INVITES_ENDPOINTS.BASE}?${queryString}`
    )
    return data
  }

  createInvitations = async ({
    emails,
    roles,
    group_id,
    program_id,
    annual_business_miles,
    program_start_date,
    state,
    zip_code
  }: {
    emails: Member['email'][]
    roles: Role['token_id'][]
    group_id: Team['token_id']
    program_id: Program['token_id']
    annual_business_miles?: number | string
    program_start_date?: string
    state?: string
    zip_code?: string
  }) => {
    const { data } = await this.axiosInstance.post<MembersInviteResponse>(
      INVITES_ENDPOINTS.CREATE_MULTIPLE_INVITES,
      {
        emails,
        roles,
        group_id,
        program_id,
        type: 'InviteTeamMember',
        membership_type: 'team_member',
        annual_business_miles,
        program_start_date,
        state,
        zip_code
      }
    )
    return data
  }

  createInvite = async ({ email, type }: InvitePayload) => {
    const { data } = await this.axiosInstance.post<{ token_id?: ID }[]>(
      INVITES_ENDPOINTS.BASE,
      {
        email,
        type
      }
    )
    return data
  }

  resendInvitation = async ({
    token_ids
  }: {
    token_ids?: Invitation['token_id'][]
  }) => {
    const { data } = await this.axiosInstance.post<EmptyObject>(
      INVITES_ENDPOINTS.RESEND_INVITES,
      {
        token_ids
      }
    )
    return data
  }

  deleteInvitation = async (
    token_id: Invitation['token_id'],
    from: 'onboarding_table' | 'invites_table' = 'invites_table'
  ) => {
    const d = await this.axiosInstance.delete<EmptyObject>(
      `${INVITES_ENDPOINTS.BASE}/${encodeURIComponent(token_id)}?from=${from}`
    )
    return d.data
  }

  reclassifyCustomPurposes = async ({ purposes }: { purposes: string[] }) => {
    const d = await this.axiosInstance.post<EmptyObject>(
      INVITES_ENDPOINTS.RECLASSIFY_PURPOSES,
      { purposes }
    )
    return d.data
  }

  acceptTeamInvite = async ({
    invite_id: token_id
  }: {
    invite_id: Invitation['token_id']
  }) => {
    const d = await this.axiosInstance.get(
      INVITES_ENDPOINTS.ACCEPT_INVITE(token_id)
    )
    return d.data
  }

  uploadCsvBulkInvite = async ({
    file,
    teamId
  }: {
    file: File
    teamId: Group['token_id']
  }) => {
    const formData = new FormData()
    formData.append('bulk_invite[data_file]', file)
    formData.append('bulk_invite[root_team_token_id]', teamId as string)

    // Axios 1.x converts FormData to JSON when Content-Type is 'application/json' (set in defaultConfig).
    // Setting null removes that header so the browser sets multipart/form-data with the correct boundary.
    const d = await this.axiosInstance.post<BulkInviteResponse>(
      INVITES_ENDPOINTS.BULK_INVITE,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return d.data
  }

  getCSVTemplate = async () => {
    const d = await this.axiosInstance.get(
      INVITES_ENDPOINTS.BULK_INVITE_TEMPLATE
    )
    return d.data
  }

  inviteAccountantClient = async ({ emails = '' }: { emails: string }) => {
    const d = await this.axiosInstance.post(
      INVITES_ENDPOINTS.CREATE_MULTIPLE_INVITES,
      {
        emails,
        type: 'InviteClient'
      }
    )
    return d.data
  }
}

export default InvitesApi
